/**
 * Process plan data and extract ingredients grouped by category
 * 
 * @param {Object} planData - Plan data with days/plans array and meals
 * @returns {Object} Categorized ingredients map
 */
export const processGroceryData = (planData) => {
  if (!planData) {
    return {};
  }

  // Support both 'days' and 'plans' array formats
  const daysArray = planData.days || planData.plans;
  
  if (!daysArray || !Array.isArray(daysArray)) {
    return {};
  }

  const ingredientMap = {};
  
  // Process each day's meals
  daysArray.forEach((day) => {
    ['breakfast', 'lunch', 'dinner'].forEach((mealType) => {
      const meals = day[mealType] || [];
      
      meals.forEach((meal) => {
        // Only process dishes (not restaurants)
        if (meal.type === 'dish' && meal.recipe) {
          meal.recipe.forEach((ingredient) => {
            const key = ingredient.name.toLowerCase();
            
            if (ingredientMap[key]) {
              // If ingredient already exists, combine quantities
              if (ingredient.amount && ingredient.measure_unit) {
                const existingAmount = ingredientMap[key].amount || 0;
                const existingUnit = ingredientMap[key].measure_unit;
                
                // If same unit, add amounts
                if (existingUnit === ingredient.measure_unit) {
                  ingredientMap[key].amount = existingAmount + ingredient.amount;
                  ingredientMap[key].quantity = `${ingredientMap[key].amount} ${existingUnit}`;
                } else {
                  // Different units, concatenate
                  const newQuantity = `${ingredient.amount} ${ingredient.measure_unit}`;
                  if (!ingredientMap[key].quantity.includes(newQuantity)) {
                    ingredientMap[key].quantity += `, ${newQuantity}`;
                  }
                }
              }
            } else {
              // Add new ingredient
              ingredientMap[key] = {
                name: ingredient.name,
                amount: ingredient.amount || null,
                measure_unit: ingredient.measure_unit || null,
                quantity: ingredient.amount && ingredient.measure_unit 
                  ? `${ingredient.amount} ${ingredient.measure_unit}` 
                  : 'As needed',
                category: ingredient.category || 'Other',
                ingredient_id: ingredient.id || null
              };
            }
          });
        }
      });
    });
  });

  // Group by category
  const categorizedIngredients = {};
  Object.values(ingredientMap).forEach((ingredient) => {
    const category = ingredient.category;
    if (!categorizedIngredients[category]) {
      categorizedIngredients[category] = [];
    }
    categorizedIngredients[category].push(ingredient);
  });

  // Sort ingredients within each category
  Object.keys(categorizedIngredients).forEach((category) => {
    categorizedIngredients[category].sort((a, b) => a.name.localeCompare(b.name));
  });

  return categorizedIngredients;
};

/**
 * Flatten categorized ingredients to a simple list for API
 * 
 * @param {Object} categorizedData - Result from processGroceryData
 * @returns {Array} Flat list of ingredients
 */
export const flattenGroceryData = (categorizedData) => {
  const flatList = [];
  
  Object.entries(categorizedData).forEach(([category, items]) => {
    items.forEach(item => {
      flatList.push({
        ingredient_name: item.name,
        ingredient_id: item.ingredient_id,
        total_amount: item.amount,
        measure_unit: item.measure_unit,
        category: category,
        quantity: item.quantity, // Keep display quantity for UI
        is_purchased: false,
        notes: ''
      });
    });
  });
  
  return flatList;
};